import RefreshIcon from '@mui/icons-material/Refresh';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { EvalRun } from './api/evalApi';
import { useListRunsQuery } from './api/evalApi';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function truncateId(id: string, maxLen = 12): string {
  return id.length > maxLen ? `${id.slice(0, maxLen)}…` : id;
}

const statusColorMap: Record<EvalRun['status'], 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'default',
  running: 'warning',
  completed: 'success',
  failed: 'error',
};

function scoreDisplay(run: EvalRun): string {
  if (run.overall_score != null) return `${Math.round(run.overall_score * 100)}%`;
  if (run.overall_pass === true) return 'Pass';
  if (run.overall_pass === false) return 'Fail';
  return '—';
}

const SKELETON_ROWS = 5;
const TABLE_COLUMNS = 6;

export default function EvalRunsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const suiteID = searchParams.get('suite_id') ?? undefined;
  const caseID = searchParams.get('case_id') ?? undefined;
  const requiresReview = searchParams.get('requires_review') === 'true';
  const isRegression = searchParams.get('is_regression') === 'true';
  const [suiteFilterDraft, setSuiteFilterDraft] = useState(suiteID ?? '');
  const [caseFilterDraft, setCaseFilterDraft] = useState(caseID ?? '');
  const [requiresReviewDraft, setRequiresReviewDraft] = useState(requiresReview);
  const [isRegressionDraft, setIsRegressionDraft] = useState(isRegression);

  const { data, isLoading, isFetching, error, refetch } = useListRunsQuery({
    suite_id: suiteID,
    case_id: caseID,
    requires_review: requiresReview || undefined,
    is_regression: isRegression || undefined,
  });

  const runs = data?.runs ?? [];
  const totalCount = data?.total_count ?? runs.length;
  const completedCount = useMemo(
    () => runs.filter((run) => run.status === 'completed').length,
    [runs]
  );
  const failedCount = useMemo(() => runs.filter((run) => run.status === 'failed').length, [runs]);

  const applyFilters = () => {
    const next = new URLSearchParams(searchParams);
    if (suiteFilterDraft.trim()) {
      next.set('suite_id', suiteFilterDraft.trim());
    } else {
      next.delete('suite_id');
    }
    if (caseFilterDraft.trim()) {
      next.set('case_id', caseFilterDraft.trim());
    } else {
      next.delete('case_id');
    }
    if (requiresReviewDraft) {
      next.set('requires_review', 'true');
    } else {
      next.delete('requires_review');
    }
    if (isRegressionDraft) {
      next.set('is_regression', 'true');
    } else {
      next.delete('is_regression');
    }
    setSearchParams(next);
  };

  const clearFilters = () => {
    setSuiteFilterDraft('');
    setCaseFilterDraft('');
    setRequiresReviewDraft(false);
    setIsRegressionDraft(false);
    const next = new URLSearchParams(searchParams);
    next.delete('suite_id');
    next.delete('case_id');
    next.delete('requires_review');
    next.delete('is_regression');
    setSearchParams(next);
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>

      {/* Stats bar */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <StatCard
          label="Total"
          value={totalCount}
          tooltip="Total runs matching your current filters"
        />
        <StatCard
          label="Completed"
          value={completedCount}
          color="success.main"
          tooltip="Runs that finished successfully"
        />
        <StatCard
          label="Failed"
          value={failedCount}
          color={failedCount > 0 ? 'error.main' : 'text.secondary'}
          tooltip="Runs that crashed or timed out before finishing"
        />
      </Stack>

      {/* Filter bar */}
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            label="Suite ID"
            size="small"
            value={suiteFilterDraft}
            onChange={(event) => setSuiteFilterDraft(event.target.value)}
            sx={{ minWidth: 160, flex: '1 1 auto' }}
          />
          <TextField
            label="Case ID"
            size="small"
            value={caseFilterDraft}
            onChange={(event) => setCaseFilterDraft(event.target.value)}
            sx={{ minWidth: 160, flex: '1 1 auto' }}
          />
          <Tooltip title="Show only runs flagged for human review">
            <Chip
              label="Needs Review"
              aria-label="Requires Review"
              variant={requiresReviewDraft ? 'filled' : 'outlined'}
              color={requiresReviewDraft ? 'primary' : 'default'}
              onClick={() => setRequiresReviewDraft((prev) => !prev)}
              sx={{ fontWeight: requiresReviewDraft ? 600 : 400 }}
            />
          </Tooltip>
          <Tooltip title="Show only runs that scored worse than before">
            <Chip
              label="Regression"
              aria-label="Regression"
              variant={isRegressionDraft ? 'filled' : 'outlined'}
              color={isRegressionDraft ? 'error' : 'default'}
              onClick={() => setIsRegressionDraft((prev) => !prev)}
              sx={{ fontWeight: isRegressionDraft ? 600 : 400 }}
            />
          </Tooltip>
          <Button variant="contained" size="small" onClick={applyFilters} sx={{ minWidth: 72 }}>
            Apply
          </Button>
          <Button variant="text" size="small" onClick={clearFilters}>
            Clear
          </Button>
        </Stack>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load runs.
        </Alert>
      )}

      {/* Runs table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Provider:Model</TableCell>
              <TableCell>Case ID</TableCell>
              <TableCell>Triggered By</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">
                <Tooltip title="Percentage of assertions that passed, or Pass/Fail">
                  <span>Score</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_COLUMNS}>
                  <EmptyState />
                </TableCell>
              </TableRow>
            ) : (
              runs.map((run) => (
                <TableRow
                  key={run.id}
                  hover
                  sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                  onClick={() => navigate(`/admin/evals/runs/${run.id}`)}
                >
                  <TableCell>
                    <Chip label={run.status} size="small" color={statusColorMap[run.status]} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {run.provider}:{run.model}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" title={run.case_id}>
                      {truncateId(run.case_id)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{run.triggered_by}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" title={new Date(run.created_at).toLocaleString()}>
                      {relativeTime(run.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {scoreDisplay(run)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/* ---------- sub-components ---------- */

function StatCard({
  label,
  value,
  color = 'text.primary',
  tooltip,
}: {
  label: string;
  value: number;
  color?: string;
  tooltip?: string;
}) {
  const card = (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1,
        flex: '1 1 0',
        textAlign: 'center',
        minWidth: 100,
      }}
    >
      <Typography variant="h5" fontWeight={700} color={color}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );

  return tooltip ? <Tooltip title={tooltip}>{card}</Tooltip> : card;
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TableRow key={i}>
          {Array.from({ length: TABLE_COLUMNS }, (_, j) => (
            <TableCell key={j}>
              <Skeleton variant="text" width={j === 0 ? 64 : '80%'} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  return (
    <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <SearchOffIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="subtitle1" fontWeight={600}>
        No eval runs found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Adjust filters or run some cases to see results here.
      </Typography>
    </Box>
  );
}
